/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

// client/src/contexts/BrandingContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const BrandingContext = createContext();

// Theme presets - inspired by popular color schemes
export const THEME_PRESETS = {
  default: {
    name: 'Default',
    primary: '#3b82f6',
    secondary: '#64748b',
    accent: '#0ea5e9',
    background: '#ffffff',
    text: '#1e293b'
  },
  gruvbox: {
    name: 'Gruvbox',
    primary: '#d65d0e',
    secondary: '#458588',
    accent: '#b16286',
    background: '#282828',
    text: '#ebdbb2'
  },
  nord: {
    name: 'Nord',
    primary: '#5e81ac',
    secondary: '#81a1c1',
    accent: '#88c0d0',
    background: '#2e3440',
    text: '#eceff4'
  },
  kanagawa: {
    name: 'Kanagawa Wave',
    primary: '#7e9cd8',
    secondary: '#7fb4ca',
    accent: '#dca561',
    background: '#1f1f28',
    text: '#dcd7ba'
  },
  catppuccin: {
    name: 'Catppuccin Mocha',
    primary: '#89b4fa',
    secondary: '#94e2d5',
    accent: '#f5c2e7',
    background: '#1e1e2e',
    text: '#cdd6f4'
  },
  dracula: {
    name: 'Dracula',
    primary: '#bd93f9',
    secondary: '#8be9fd',
    accent: '#50fa7b',
    background: '#282a36',
    text: '#f8f8f2'
  },
  solarized_light: {
    name: 'Solarized Light',
    primary: '#268bd2',
    secondary: '#2aa198',
    accent: '#859900',
    background: '#fdf6e3',
    text: '#657b83'
  },
  solarized_dark: {
    name: 'Solarized Dark',
    primary: '#268bd2',
    secondary: '#2aa198',
    accent: '#b58900',
    background: '#002b36',
    text: '#839496'
  },
  custom: {
    name: 'Custom',
    primary: '#3b82f6',
    secondary: '#64748b',
    accent: '#0ea5e9',
    background: '#ffffff',
    text: '#1e293b'
  }
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    schoolName: 'Loading...',
    logo: null,
    contactEmail: '',
    contactPhone: '',
    schoolAddress: '',
    primaryColor: THEME_PRESETS.default.primary,
    secondaryColor: THEME_PRESETS.default.secondary
  });

  const [demo, setDemo] = useState(null);

  const [theme, setTheme] = useState('custom');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch branding configuration from public API
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await fetch('/api/config/public');
        if (!response.ok) {
          throw new Error('Failed to fetch branding configuration');
        }

        const data = await response.json();
        const brandingConfig = data.config?.branding || {};

        setBranding({
          schoolName: brandingConfig.school_name || 'Safety Training',
          logo: brandingConfig.school_logo_url,
          contactEmail: brandingConfig.contact_email || '',
          contactPhone: brandingConfig.contact_phone || '',
          schoolAddress: brandingConfig.school_address || '',
          primaryColor: brandingConfig.primary_color || THEME_PRESETS.default.primary,
          secondaryColor: brandingConfig.secondary_color || THEME_PRESETS.default.secondary
        });

        setDemo(data.config?.demo || null);

        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching branding:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchBranding();
  }, []);

  // Apply theme colors to CSS variables
  useEffect(() => {
    const currentTheme = THEME_PRESETS[theme] || THEME_PRESETS.default;

    // If custom theme, use branding colors
    const colors = theme === 'custom' ? {
      primary: branding.primaryColor,
      secondary: branding.secondaryColor,
      accent: branding.primaryColor,
      background: '#ffffff',
      text: '#1e293b'
    } : currentTheme;

    // Apply CSS variables
    const root = document.documentElement;
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-text', colors.text);

    // Store theme preference
    localStorage.setItem('preferredTheme', theme);
  }, [theme, branding.primaryColor, branding.secondaryColor]);

  // Load theme preference on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('preferredTheme');
    if (savedTheme && THEME_PRESETS[savedTheme]) {
      setTheme(savedTheme);
    }
  }, []);

  const value = {
    branding,
    demo,
    theme,
    setTheme,
    themes: THEME_PRESETS,
    isLoading,
    error,
    refreshBranding: async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/api/config/public');
        const data = await response.json();
        const brandingConfig = data.config?.branding || {};

        setBranding({
          schoolName: brandingConfig.school_name || 'Safety Training',
          logo: brandingConfig.school_logo_url,
          contactEmail: brandingConfig.contact_email || '',
          contactPhone: brandingConfig.contact_phone || '',
          schoolAddress: brandingConfig.school_address || '',
          primaryColor: brandingConfig.primary_color || THEME_PRESETS.default.primary,
          secondaryColor: brandingConfig.secondary_color || THEME_PRESETS.default.secondary
        });
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext;